import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getGridOperators } from 'app/entities/grid-operator/grid-operator.reducer';
import { getEntities as getProductOfferingPrices } from 'app/entities/product-offering-price/product-offering-price.reducer';
import { MeterType } from 'app/shared/model/enumerations/meter-type.model';
import { ProductType } from 'app/shared/model/enumerations/product-type.model';
import { GridOperatorCostType } from 'app/shared/model/enumerations/grid-operator-cost-type.model';
import { createEntity, getEntity, reset, updateEntity } from './grid-operator-capacity.reducer';

export const GridOperatorCapacityUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const gridOperators = useAppSelector(state => state.gridOperator.entities);
  const productOfferingPrices = useAppSelector(state => state.productOfferingPrice.entities);
  const gridOperatorCapacityEntity = useAppSelector(state => state.gridOperatorCapacity.entity);
  const loading = useAppSelector(state => state.gridOperatorCapacity.loading);
  const updating = useAppSelector(state => state.gridOperatorCapacity.updating);
  const updateSuccess = useAppSelector(state => state.gridOperatorCapacity.updateSuccess);
  const meterTypeValues = Object.keys(MeterType);
  const productTypeValues = Object.keys(ProductType);
  const gridOperatorCostTypeValues = Object.keys(GridOperatorCostType);

  const handleClose = () => {
    navigate('/grid-operator-capacity');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getGridOperators({}));
    dispatch(getProductOfferingPrices({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.trancheLowerBound !== undefined && typeof values.trancheLowerBound !== 'number') {
      values.trancheLowerBound = Number(values.trancheLowerBound);
    }
    if (values.trancheUpperBound !== undefined && typeof values.trancheUpperBound !== 'number') {
      values.trancheUpperBound = Number(values.trancheUpperBound);
    }

    const entity = {
      ...gridOperatorCapacityEntity,
      ...values,
      gridOperator: gridOperators.find(it => it.id.toString() === values.gridOperator?.toString()),
      productOfferingPrices: mapIdList(values.productOfferingPrices),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          meterType: 'G4_G6',
          productType: 'ELECTRICITY',
          type: 'G4_G6_UP_TO_10_M3H_LESS_THAN_500_M3_PER_YEAR',
          ...gridOperatorCapacityEntity,
          gridOperator: gridOperatorCapacityEntity?.gridOperator?.id,
          productOfferingPrices: gridOperatorCapacityEntity?.productOfferingPrices?.map(e => e.id.toString()),
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2
            id="crawlerBackendApp.gridOperatorCapacity.home.createOrEditLabel"
            data-cy="GridOperatorCapacityCreateUpdateHeading"
          >
            <Translate contentKey="crawlerBackendApp.gridOperatorCapacity.home.createOrEditLabel">
              Create or edit a GridOperatorCapacity
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="grid-operator-capacity-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.gridOperatorCapacity.capacityCode')}
                id="grid-operator-capacity-capacityCode"
                name="capacityCode"
                data-cy="capacityCode"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.gridOperatorCapacity.description')}
                id="grid-operator-capacity-description"
                name="description"
                data-cy="description"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.gridOperatorCapacity.meterType')}
                id="grid-operator-capacity-meterType"
                name="meterType"
                data-cy="meterType"
                type="select"
              >
                {meterTypeValues.map(meterType => (
                  <option value={meterType} key={meterType}>
                    {translate(`crawlerBackendApp.MeterType.${meterType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.gridOperatorCapacity.productType')}
                id="grid-operator-capacity-productType"
                name="productType"
                data-cy="productType"
                type="select"
              >
                {productTypeValues.map(productType => (
                  <option value={productType} key={productType}>
                    {translate(`crawlerBackendApp.ProductType.${productType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.gridOperatorCapacity.type')}
                id="grid-operator-capacity-type"
                name="type"
                data-cy="type"
                type="select"
              >
                {gridOperatorCostTypeValues.map(gridOperatorCostType => (
                  <option value={gridOperatorCostType} key={gridOperatorCostType}>
                    {translate(`crawlerBackendApp.GridOperatorCostType.${gridOperatorCostType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.gridOperatorCapacity.trancheLowerBound')}
                id="grid-operator-capacity-trancheLowerBound"
                name="trancheLowerBound"
                data-cy="trancheLowerBound"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.gridOperatorCapacity.trancheUpperBound')}
                id="grid-operator-capacity-trancheUpperBound"
                name="trancheUpperBound"
                data-cy="trancheUpperBound"
                type="text"
              />
              <ValidatedField
                id="grid-operator-capacity-gridOperator"
                name="gridOperator"
                data-cy="gridOperator"
                label={translate('crawlerBackendApp.gridOperatorCapacity.gridOperator')}
                type="select"
              >
                <option value="" key="0" />
                {gridOperators
                  ? gridOperators.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.ean}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.gridOperatorCapacity.productOfferingPrice')}
                id="grid-operator-capacity-productOfferingPrice"
                data-cy="productOfferingPrice"
                type="select"
                multiple
                name="productOfferingPrices"
              >
                <option value="" key="0" />
                {productOfferingPrices
                  ? productOfferingPrices.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/grid-operator-capacity"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default GridOperatorCapacityUpdate;
